import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ClientFormData } from '../../types/clients';
import { clientService } from '../../services/clientService';

interface ClientFormProps {
  initialData?: Partial<ClientFormData>;
  isEdit?: boolean;
  clientId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ClientForm: React.FC<ClientFormProps> = ({
  initialData = {},
  isEdit = false,
  clientId,
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState<ClientFormData>({
    businessName: initialData.businessName || '',
    dbaName: initialData.dbaName || '',
    ein: initialData.ein || '',
    businessType: initialData.businessType || 'LLC',
    industry: initialData.industry || '',
    yearEstablished: initialData.yearEstablished || new Date().getFullYear(),
    annualRevenue: initialData.annualRevenue,
    numberOfEmployees: initialData.numberOfEmployees,
    address: {
      street: initialData.address?.street || '',
      city: initialData.address?.city || '',
      state: initialData.address?.state || '',
      zipCode: initialData.address?.zipCode || '',
      country: initialData.address?.country || 'US',
    },
    contactInfo: {
      primaryContactName: initialData.contactInfo?.primaryContactName || '',
      primaryContactTitle: initialData.contactInfo?.primaryContactTitle || '',
      phone: initialData.contactInfo?.phone || '',
      email: initialData.contactInfo?.email || '',
      website: initialData.contactInfo?.website || '',
    },
    creditScore: initialData.creditScore ?? undefined,
    riskRating: initialData.riskRating || 'Medium',
    status: initialData.status || 'Active',
    notes: initialData.notes || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  const handleContactChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      contactInfo: {
        ...prev.contactInfo,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEdit && clientId) {
        await clientService.updateClient(clientId, formData);
      } else {
        await clientService.createClient(formData);
      }
      onSuccess?.();
    } catch (err) {
      setError(isEdit ? 'Failed to update client' : 'Failed to create client');
      console.error('Form submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h5" gutterBottom>
        {isEdit ? 'Edit Client' : 'Add New Client'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Business Information */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Business Information
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Business Name"
                  value={formData.businessName}
                  onChange={(e) => handleInputChange('businessName', e.target.value)}
                  required
                  aria-label="Enter the legal business name"
                />
                <TextField
                  fullWidth
                  label="DBA Name (Optional)"
                  value={formData.dbaName}
                  onChange={(e) => handleInputChange('dbaName', e.target.value)}
                  aria-label="Enter doing business as name if different from legal name"
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="EIN"
                  value={formData.ein}
                  onChange={(e) => handleInputChange('ein', e.target.value)}
                  placeholder="12-3456789"
                  required
                  aria-label="Enter employer identification number"
                />
                <FormControl fullWidth required>
                  <InputLabel>Business Type</InputLabel>
                  <Select
                    value={formData.businessType}
                    label="Business Type"
                    onChange={(e) => handleInputChange('businessType', e.target.value)}
                    aria-label="Select the type of business entity"
                  >
                    <MenuItem value="LLC">LLC</MenuItem>
                    <MenuItem value="Corporation">Corporation</MenuItem>
                    <MenuItem value="Partnership">Partnership</MenuItem>
                    <MenuItem value="Sole Proprietorship">Sole Proprietorship</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Industry"
                  value={formData.industry}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  required
                  aria-label="Enter the industry classification"
                />
                <TextField
                  fullWidth
                  label="Year Established"
                  type="number"
                  value={formData.yearEstablished}
                  onChange={(e) => handleInputChange('yearEstablished', parseInt(e.target.value))}
                  required
                  inputProps={{ min: 1800, max: new Date().getFullYear() }}
                  aria-label="Enter the year the business was established"
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Annual Revenue (Optional)"
                  type="number"
                  value={formData.annualRevenue || ''}
                  onChange={(e) => handleInputChange('annualRevenue', parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  InputProps={{ startAdornment: '$' }}
                  aria-label="Enter annual revenue in USD"
                />
                <TextField
                  fullWidth
                  label="Number of Employees (Optional)"
                  type="number"
                  value={formData.numberOfEmployees || ''}
                  onChange={(e) => handleInputChange('numberOfEmployees', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  aria-label="Enter the number of employees"
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Primary Contact Name"
                  value={formData.contactInfo.primaryContactName}
                  onChange={(e) => handleContactChange('primaryContactName', e.target.value)}
                  required
                  aria-label="Enter the primary contact person's name"
                />
                <TextField
                  fullWidth
                  label="Contact Title (Optional)"
                  value={formData.contactInfo.primaryContactTitle}
                  onChange={(e) => handleContactChange('primaryContactTitle', e.target.value)}
                  aria-label="Enter the primary contact's job title"
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.contactInfo.phone}
                  onChange={(e) => handleContactChange('phone', e.target.value)}
                  required
                  aria-label="Enter contact phone number"
                />
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.contactInfo.email}
                  onChange={(e) => handleContactChange('email', e.target.value)}
                  required
                  aria-label="Enter contact email address"
                />
              </Stack>

              <TextField
                fullWidth
                label="Website (Optional)"
                value={formData.contactInfo.website}
                onChange={(e) => handleContactChange('website', e.target.value)}
                aria-label="Enter company website URL"
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Address */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Business Address
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.address.street}
                onChange={(e) => handleAddressChange('street', e.target.value)}
                required
                aria-label="Enter street address"
              />

              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.address.city}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  required
                  aria-label="Enter city"
                />
                <TextField
                  label="State"
                  value={formData.address.state}
                  onChange={(e) => handleAddressChange('state', e.target.value)}
                  required
                  inputProps={{ maxLength: 2 }}
                  sx={{ width: '120px' }}
                  aria-label="Enter two-letter state code"
                />
                <TextField
                  label="ZIP Code"
                  value={formData.address.zipCode}
                  onChange={(e) => handleAddressChange('zipCode', e.target.value)}
                  required
                  sx={{ width: '150px' }}
                  aria-label="Enter ZIP code"
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Risk Assessment */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Risk Assessment
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    aria-label="Select client status"
                  >
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                    <MenuItem value="Suspended">Suspended</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Risk Rating</InputLabel>
                  <Select
                    value={formData.riskRating}
                    label="Risk Rating"
                    onChange={(e) => handleInputChange('riskRating', e.target.value)}
                    aria-label="Select risk rating"
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Very High">Very High</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <TextField
                fullWidth
                label="Credit Score (Optional)"
                type="number"
                value={formData.creditScore || ''}
                onChange={(e) => handleInputChange('creditScore', parseInt(e.target.value) || 0)}
                inputProps={{ min: 300, max: 850 }}
                aria-label="Enter business credit score"
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Additional Notes
            </Typography>
            <TextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={4}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              aria-label="Enter any additional notes about the client"
            />
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={loading}
            aria-label="Cancel form submission"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            aria-label={isEdit ? 'Update client information' : 'Create new client'}
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Client' : 'Create Client')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ClientForm;